import { Alert, AlertIcon, List, ListItem, Stack, Text } from '@chakra-ui/react';
import { Show } from '@pelicargo/ui';

import { useCurrentDraft } from '../../../hooks/useCurrentDraft';
import { BookingConfirmationPayload } from './payload';

type Props = {
  booking: BookingConfirmationPayload | null;
};

/**
 * Everything the reviewer needs warning about before trusting the flights: how the email was
 * linked, what the extraction could not read, and what the quote says that the email does not.
 */
export const ReviewAlerts = ({ booking }: Props) => {
  const { draft } = useCurrentDraft();
  const unconfirmedCount = booking?.unconfirmedQuoteSegmentIds.length ?? 0;

  // Three ways to have no quote to apply onto, each with a different fix. Every one leaves the draft
  // un-applyable: the flights go on by hand, and the draft is discarded so the queue stays honest.
  const hasNoRequest = !draft?.request_id;
  const hasRequestButNoCarrier = !!draft?.request_id && !draft?.provider_id;
  const hasCarrierButNoQuote = !!draft?.request_id && !!draft?.provider_id && !draft?.quote_id;

  return (
    <>
      <Show if={hasNoRequest}>
        <Alert status="warning" borderRadius="md" alignItems="flex-start">
          <AlertIcon />
          <Stack spacing="0.5" fontSize="sm">
            <Text fontWeight="medium">No request found for this confirmation — nothing to apply it to</Text>
            <List spacing="0" pl="1">
              <ListItem>
                • The subject names no R-number we hold, the AWB is on none of our quotes, and it is not a reply in one
                of our threads
              </ListItem>
              <ListItem>
                • If it is ours, find the request and enter the flights by hand, then discard this draft with the
                R-number as the reason
              </ListItem>
              <ListItem>
                • If it is not ours (wrong recipient, a booking made outside the platform), discard it
              </ListItem>
            </List>
          </Stack>
        </Alert>
      </Show>

      <Show if={hasRequestButNoCarrier}>
        <Alert status="warning" borderRadius="md" alignItems="flex-start">
          <AlertIcon />
          <Stack spacing="0.5" fontSize="sm">
            <Text fontWeight="medium">Request found, but the carrier could not be identified</Text>
            <List spacing="0" pl="1">
              <ListItem>
                • The sender is not a contact on any carrier, and no flight number named an airline we hold
              </ListItem>
              <ListItem>• Enter the flights on the right quote by hand, then discard this draft</ListItem>
              <ListItem>• Adding the sender as a contact on the carrier makes their next confirmation match</ListItem>
            </List>
          </Stack>
        </Alert>
      </Show>

      <Show if={hasCarrierButNoQuote}>
        <Alert status="warning" borderRadius="md" alignItems="flex-start">
          <AlertIcon />
          <Stack spacing="0.5" fontSize="sm">
            <Text fontWeight="medium">
              {draft?.provider_id} has no quote on R{draft?.request_id} — this confirms a booking we never quoted
            </Text>
            <List spacing="0" pl="1">
              <ListItem>• Most likely booked directly with the carrier, outside the platform</ListItem>
              <ListItem>
                • If it should be on the platform, create the quote on the request and enter the flights, then discard
                this draft
              </ListItem>
            </List>
          </Stack>
        </Alert>
      </Show>

      <Show if={booking?.quoteLinkage === 'guessed' && booking.segments.length > 0}>
        <Alert status="info" borderRadius="md" alignItems="flex-start">
          <AlertIcon />
          <Stack spacing="0.5" fontSize="sm">
            <Text fontWeight="medium">Quote picked by best guess — the email named no AWB we hold</Text>
            <List spacing="0" pl="1">
              <ListItem>• Flight comparisons are against that guess</ListItem>
              <ListItem>• Check it is the right quote before trusting them</ListItem>
            </List>
          </Stack>
        </Alert>
      </Show>

      <Show if={booking?.kind === 'not_a_confirmation'}>
        <Alert status="warning" borderRadius="md">
          <AlertIcon />
          <Text>
            On a closer read this email does not confirm a booking — it may be a request to book, a decline or a
            schedule notice. Any flights it mentions are still listed below.
          </Text>
        </Alert>
      </Show>

      <Show if={unconfirmedCount > 0}>
        <Alert status="warning" borderRadius="md" alignItems="flex-start">
          <AlertIcon />
          <Stack spacing="0.5" fontSize="sm">
            <Text fontWeight="medium">
              {unconfirmedCount} quote flight{unconfirmedCount === 1 ? '' : 's'} not mentioned in this email
            </Text>
            <List spacing="0" pl="1">
              <ListItem>• Only part of the routing is confirmed — a partial booking</ListItem>
              <ListItem>• Check the remaining flights with the carrier</ListItem>
            </List>
          </Stack>
        </Alert>
      </Show>

      <Show if={!!booking?.attachmentsSkippedForBudget}>
        <Alert status="warning" borderRadius="md">
          <AlertIcon />
          <Text>
            This email carried attachments that were not read — the hourly image budget was exhausted. Any flights in
            them are missing below. Open the source email and check.
          </Text>
        </Alert>
      </Show>

      <Show if={!booking}>
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <Text>
            No flights were extracted from this email — read the source email below and enter them on the request by
            hand.
          </Text>
        </Alert>
      </Show>
    </>
  );
};
